/**
 * "What goes where?" — the reference for classifying spending.
 *
 * Written to be read once and settle arguments, not to be admired. The
 * household's own percentages are shown rather than the textbook 50/30/20,
 * because that split is configurable and quoting numbers the user has not
 * chosen would be actively misleading.
 */
import { BUCKET_LABELS } from '@et/core';

interface Props {
  needsPct: number;
  wantsPct: number;
  savingsPct: number;
  onClose: () => void;
}

interface BucketCard {
  key: 'NEEDS' | 'WANTS' | 'SAVINGS';
  pct: number;
  test: string;
  counts: string[];
  note: string;
}

export function BucketGuide({ needsPct, wantsPct, savingsPct, onClose }: Props) {
  const cards: BucketCard[] = [
    {
      key: 'NEEDS',
      pct: needsPct,
      test: 'Stop paying it and something actually breaks.',
      counts: [
        'Rent or mortgage, service charges, repairs',
        'Electricity, heating, water, internet, phone plan',
        'Groceries you cook at home',
        'Getting to work — transport pass, fuel, car insurance',
        'Health and household insurance, medication',
        'The MINIMUM payment on any debt',
      ],
      note: 'Eviction, no heating, no way to reach work, a default on your credit record — those are consequences, not inconvenience.',
    },
    {
      key: 'WANTS',
      pct: wantsPct,
      test: 'Stop paying it and nothing breaks; life is just less pleasant.',
      counts: [
        'Restaurants, cafés, takeaway and delivery',
        'Streaming, cinema, games, events',
        'Holidays and days out',
        'Clothes and electronics beyond replacing what wore out',
        'Gym, hobbies, haircuts, cosmetics',
        'Gifts and donations',
      ],
      note: 'This is the only bucket you can genuinely change this month. Needs move slowly; Savings is the point of the exercise. Wants is where the decisions are.',
    },
    {
      key: 'SAVINGS',
      pct: savingsPct,
      test: 'The money is still yours afterwards.',
      counts: [
        'Emergency fund',
        'Pension and investments',
        'Saving towards a specific purchase',
        'Debt repayment ABOVE the minimum',
      ],
      note: 'Paying more than the minimum on a loan belongs here, not in Needs: it buys down future interest, and future you keeps the difference.',
    },
  ];

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="How the buckets work">
      <div className="modal">
        <header className="modal-head">
          <h2>How the buckets work</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="guide">
          <p className="guide-lead">
            Every expense lands in one of three buckets. Your split is{' '}
            <strong>
              {needsPct}/{wantsPct}/{savingsPct}
            </strong>
            . One question settles most cases:{' '}
            <em>what actually happens if I stop paying this?</em>
          </p>

          {cards.map((card) => (
            <section className={`guide-card guide-${card.key.toLowerCase()}`} key={card.key}>
              <h3>
                <span className={`dot dot-${card.key.toLowerCase()}`} aria-hidden="true" />
                {BUCKET_LABELS[card.key]}
                <span className="guide-pct">{card.pct}%</span>
              </h3>
              <p className="guide-test">{card.test}</p>
              <ul>
                {card.counts.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="guide-note">{card.note}</p>
            </section>
          ))}

          <section className="guide-rule">
            <h3>The upgrade rule</h3>
            <p>
              When something is partly necessary and partly not, the basic version is a Need and the
              difference is a Want. A car to reach work is a Need; the premium on a nicer one is a
              Want. Groceries are a Need; the €90 bottle of wine bought in the same shop is not.
            </p>
            <p>
              That is what <strong>Split</strong> is for: put one receipt across two categories, and
              use the bucket override on the line that does not match its category.
            </p>
          </section>

          <section className="guide-rule">
            <h3>The ones people argue about</h3>
            <dl className="guide-faq">
              <dt>Debt</dt>
              <dd>Minimum payment is a Need. Anything above it is Savings.</dd>
              <dt>Phone</dt>
              <dd>The plan is a Need. The flagship handset is a Want.</dd>
              <dt>Gym</dt>
              <dd>A Want — unless a doctor prescribed it, in which case Health.</dd>
              <dt>Groceries vs eating out</dt>
              <dd>Cooking at home is a Need. A restaurant is a Want, even on a Tuesday.</dd>
              <dt>Emergency fund</dt>
              <dd>Savings. It feels like safety, but the money is still yours.</dd>
              <dt>Insurance</dt>
              <dd>A Need. It is the thing that stops one bad month becoming a bad year.</dd>
            </dl>
          </section>

          <p className="guide-foot">
            The percentages are a target, not a verdict. If your Needs run above {needsPct}%, that is
            information about your rent, not a personal failing — and the split is yours to change.
          </p>
        </div>
      </div>
    </div>
  );
}
